import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, RefreshCw, Building2, UserMinus, X, Send, UserCheck } from "lucide-react";
import { toast } from "../../components/ui/Toast";
import StatusBadge from "../../components/ui/StatusBadge";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const PAGE_SIZE = 20;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const cardCls =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const inputCls =
  "w-full p-2.5 rounded-lg bg-black/40 border border-white/20 text-white placeholder-white/30 " +
  "outline-none focus:border-[#00FF99]/60 text-sm";

const secondaryBtn =
  "inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white/90 px-3 py-2 " +
  "rounded-lg hover:bg-white/10 transition text-sm";

const deactivateBtn =
  "inline-flex items-center gap-2 px-3 py-2 rounded-lg transition text-sm " +
  "bg-[rgba(255,50,50,0.1)] border border-[rgba(255,50,50,0.3)] text-[#ff5555] " +
  "hover:bg-[rgba(255,50,50,0.2)]";

const deactivateFilledBtn =
  "inline-flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold " +
  "bg-[#ff5555] text-white hover:bg-[#ff3333] disabled:opacity-50";

const reactivateBtn =
  "inline-flex items-center gap-2 px-3 py-2 rounded-lg transition text-sm " +
  "bg-[#00FF99]/10 border border-[#00FF99]/30 text-[#00FF99] hover:bg-[#00FF99]/20";

function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`${cardCls} max-w-md w-full p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#00FF99]">{title}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function SuperadminUsers() {
  const [orgs, setOrgs]       = useState([]);
  const [users, setUsers]     = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError]     = useState("");
  const [search, setSearch]   = useState("");
  const [orgId, setOrgId]     = useState("");  // "" = no selection → empty state
  const [page, setPage]       = useState(1);
  const [deleteUser, setDeleteUser] = useState(null);  // user pending confirmation
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast]   = useState(null);
  const [resendingId, setResendingId] = useState(null);  // uid currently in-flight
  const [showInactive, setShowInactive] = useState(false); // hide deactivated users by default

  // current logged-in user (to block self-delete in UI as well)
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  // auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // Load orgs once for the prominent picker
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/onboarding/orgs`, { headers: authHeaders() });
        const data = res.ok ? await res.json() : [];
        setOrgs(Array.isArray(data) ? data : []);
      } catch {
        setOrgs([]);
      } finally {
        setOrgsLoading(false);
      }
    })();
  }, []);

  // Fetch users only when an org is selected. `active=true` by default
  // (backend accepts "true"/"false"/"ALL"); when the admin ticks "Show
  // inactive" we pass "ALL" so deactivated rows come back too.
  const loadUsers = useCallback(async (selectedOrgId, includeInactive) => {
    if (!selectedOrgId) {
      setUsers([]);
      setUsersLoading(false);
      return;
    }
    setUsersLoading(true);
    setError("");
    try {
      const activeParam = includeInactive ? "ALL" : "true";
      const url = `${API_BASE}/api/admin/users`
        + `?org_id=${encodeURIComponent(selectedOrgId)}`
        + `&active=${activeParam}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || `Failed to load users (${res.status})`);
        setUsers([]);
        return;
      }
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setError("Network error.");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(orgId, showInactive); }, [orgId, showInactive, loadUsers]);

  // Reset to page 1 when search, org, or inactive-toggle changes
  useEffect(() => { setPage(1); }, [search, orgId, showInactive]);

  // Search filters within the already-org-scoped user list. Also hides
  // locally-deactivated rows when the toggle is off, so the just-clicked
  // user disappears without waiting for a refetch.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (!showInactive && u.is_active === false) return false;
      if (!q) return true;
      const hay = `${u.full_name || ""} ${u.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, search, showInactive]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleActive = async (u) => {
    const prev = u.is_active;
    // Optimistic: flip is_active AND derive the status field so the
    // <StatusBadge> updates immediately. Backend re-derives on next
    // fetch; toggleActive doesn't touch has_logged_in.
    const optimisticStatus = !prev ? "active" : "inactive";
    setUsers((rows) => rows.map((r) =>
      r.id === u.id ? { ...r, is_active: !prev, status: optimisticStatus } : r
    ));
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${u.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ is_active: !prev }),
      });
      if (!res.ok) throw new Error();
      setToast({
        message: !prev ? "User reactivated" : "User deactivated",
        tone: "ok",
      });
    } catch {
      setUsers((rows) => rows.map((r) =>
        r.id === u.id ? { ...r, is_active: prev, status: u.status } : r
      ));
      setToast({ message: "Could not update user status.", tone: "err" });
    }
  };

  // Resend a fresh set-password link for a PENDING user. Reuses the same
  // status logic as StatusBadge — only visible while the user hasn't
  // completed first login. Backend returns 400 if they already have.
  const resendInvite = async (u) => {
    if (!u?.id || resendingId) return;
    setResendingId(u.id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${u.id}/resend-invite`, {
        method: "POST",
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      const data = body?.data || body;
      if (!res.ok) {
        const detail = typeof body?.detail === "string" ? body.detail
                     : typeof data?.message === "string" ? data.message
                     : `Failed to resend invite (${res.status})`;
        setToast({ message: detail, tone: "err" });
        return;
      }
      setToast({
        message: data?.message || `Invite re-sent to ${u.email}.`,
        tone: "ok",
      });
    } catch {
      setToast({ message: "Network error.", tone: "err" });
    } finally {
      setResendingId(null);
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteUser) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${deleteUser.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDeleteError(data.detail || "Failed to deactivate user.");
        return;
      }
      // Soft-delete: mark inactive locally. If "Show inactive" is off
      // the row disappears naturally; if on, it stays visible with a
      // Reactivate action.
      setUsers((rows) => rows.map((r) =>
        r.id === deleteUser.id
          ? { ...r, is_active: false, status: "inactive" }
          : r
      ));
      setDeleteUser(null);
      setToast({ message: "User deactivated", tone: "ok" });
    } catch {
      setDeleteError("Network error.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const selectedOrg = orgs.find((o) => o.id === orgId);

  return (
    <div className="space-y-4">
      {/* PROMINENT Org picker */}
      <div className={`${cardCls} p-5`}>
        <div className="flex items-center gap-3 mb-2">
          <Building2 size={18} className="text-[#00FF99]" />
          <span className="text-xs uppercase tracking-wider text-white/50">
            Select an organisation
          </span>
        </div>
        <select
          className={inputCls + " text-base h-12"}
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          disabled={orgsLoading}
        >
          <option value="">— Choose an organisation —</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>{o.name || o.slug}</option>
          ))}
        </select>
        {selectedOrg && (
          <p className="text-xs text-white/50 mt-2">
            Showing users in <span className="text-white">{selectedOrg.name || selectedOrg.slug}</span>
          </p>
        )}
      </div>

      {!orgId ? (
        // EMPTY STATE — no org selected
        <div className={`${cardCls} p-12 text-center`}>
          <Building2 size={32} className="mx-auto text-white/20 mb-3" />
          <p className="text-white/60 text-sm">
            Select an organisation to view users
          </p>
          <p className="text-white/30 text-xs mt-1">
            Pick from the dropdown above to see members of that tenant
          </p>
        </div>
      ) : (
        <>
          {/* Search bar — only after an org is picked */}
          <div className={`${cardCls} p-4 flex flex-wrap items-center gap-3`}>
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                className={inputCls + " pl-9"}
                placeholder={`Search within ${selectedOrg?.name || "this org"}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-white/70 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 rounded accent-[#00FF99]"
              />
              Show inactive
            </label>
            <button onClick={() => loadUsers(orgId, showInactive)} className={secondaryBtn} title="Refresh">
              <RefreshCw size={14} />
            </button>
          </div>

          {/* Table */}
          <div className={`${cardCls} overflow-hidden`}>
            {usersLoading ? (
              <div className="p-8 text-center text-white/50">Loading users…</div>
            ) : error ? (
              <div className="p-8 text-center text-red-400">{error}</div>
            ) : pageRows.length === 0 ? (
              <div className="p-8 text-center text-white/50">
                {users.length === 0
                  ? "No users in this organisation yet."
                  : "No users match your search."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-white/60 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-left px-4 py-3">Role</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((u) => (
                      <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                        <td className="px-4 py-3 text-white">{u.full_name || "—"}</td>
                        <td className="px-4 py-3 text-white/80">{u.email}</td>
                        <td className="px-4 py-3 text-white/70 font-mono text-xs">{u.role || "—"}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={u.status || (u.is_active ? "active" : "inactive")} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2 flex-wrap">
                            {(u.status === "pending") ? (
                              // pending → Resend invite (Phase 3b)
                              <button
                                onClick={() => resendInvite(u)}
                                disabled={resendingId === u.id}
                                className={secondaryBtn + " disabled:opacity-40 disabled:cursor-wait"}
                                title="Send a fresh set-password email (1h link)"
                              >
                                <Send size={14} />
                                {resendingId === u.id ? "Sending…" : "Resend invite"}
                              </button>
                            ) : (u.is_active === false) ? (
                              // inactive → Reactivate (reuses the existing PATCH endpoint)
                              <button
                                onClick={() => toggleActive(u)}
                                className={reactivateBtn}
                                title="Reactivate this user"
                              >
                                <UserCheck size={14} />
                                Reactivate
                              </button>
                            ) : (
                              // active → Deactivate. Uses the confirm modal — it soft-
                              // deletes via DELETE, which is safer than the silent
                              // PATCH toggle for a destructive action.
                              <button
                                onClick={() => { setDeleteError(""); setDeleteUser(u); }}
                                disabled={currentUser?.id === u.id}
                                className={deactivateBtn + " disabled:opacity-40 disabled:cursor-not-allowed"}
                                title={currentUser?.id === u.id
                                  ? "You cannot deactivate your own account"
                                  : "Deactivate this user"}
                              >
                                <UserMinus size={14} /> Deactivate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!usersLoading && filtered.length > 0 && (
            <div className="flex items-center justify-between text-xs text-white/50">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}
                –
                {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className={secondaryBtn + " disabled:opacity-40 disabled:cursor-not-allowed"}
                >
                  ← Prev
                </button>
                <span className="px-3 py-2">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className={secondaryBtn + " disabled:opacity-40 disabled:cursor-not-allowed"}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {deleteUser && (
        <Modal title="Deactivate User" onClose={() => !deleteBusy && setDeleteUser(null)}>
          <div className="space-y-3">
            <p className="text-sm text-white/80">
              Deactivate{" "}
              <span className="text-white font-medium">{deleteUser.full_name || deleteUser.email}</span>?
              They will lose access immediately and be hidden from the default
              user list. Their records are kept, and you can reactivate them
              later using the "Show inactive" toggle.
            </p>

            {deleteError && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-2 text-sm">
                {deleteError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setDeleteUser(null)}
                disabled={deleteBusy}
                className={secondaryBtn}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteUser}
                disabled={deleteBusy}
                className={deactivateFilledBtn}
              >
                <UserMinus size={14} />
                {deleteBusy ? "Deactivating…" : "Deactivate user"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && (
        <div
          className={
            "fixed bottom-6 right-6 z-[60] px-4 py-3 rounded-lg shadow-xl border " +
            (toast.tone === "ok"
              ? "bg-[#00FF99]/15 border-[#00FF99]/40 text-[#00FF99]"
              : "bg-yellow-500/15 border-yellow-500/40 text-yellow-200")
          }
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
